/**
 * Shared type + helpers for advisor-uploaded documents.
 *
 * FormShell owns the storage so files persist across step navigation
 * (leaving the AdditionalDocuments step and returning later shouldn't
 * lose an already-picked file) and so the downstream PdfGeneration step
 * can list them alongside filled PDF templates and include them in
 * "Download all". The AdditionalDocuments step is a controlled UI that
 * writes to this store via the callback FormShell threads down.
 */
import type { WorkflowTemplate } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';
import { renderFilename } from './renderFilename';

/**
 * Where a picked file is in its journey to S3.
 *
 * `'local'` is the resting state in originator/approver PREVIEW, which never uploads — see
 * `useAdditionalDocumentUpload`. In advisor runtime a file moves local → uploading →
 * uploaded / failed.
 *
 * Note that `'uploaded'` does NOT mean readable: the object lands untagged and the bucket policy
 * denies reads until GuardDuty clears it (ADR-0024). That is what `FileState` tracks — a file is
 * only usable once BOTH have resolved.
 */
export type UploadState = 'local' | 'uploading' | 'uploaded' | 'failed';

/**
 * How far a successfully uploaded file has got towards being deliverable to BMO (ADR-0024,
 * ADR-0025).
 *
 * Kept separate from `UploadState` rather than folded into it because they answer different
 * questions — "did the bytes arrive?" and "is the file usable?" — and only the first is under our
 * control. A file that uploaded perfectly can still be refused.
 *
 * `'processing'` covers FOUR server-side stages: the malware scan, the PDF conversion the
 * originator's entry may require, and the malware scan on the conversion's own output. They are
 * one state here on purpose. None of them is something an advisor can act on differently, and
 * naming the malware check to an uploader is free reconnaissance for anyone probing what gets
 * through — so the UI says "Uploading…" throughout and the distinctions stay server-side.
 *
 * Advisor runtime moves a file processing → ready / infected / blocked, or gives up at `timedOut`.
 * Preview never sets this at all, which is what keeps the Continue gate inert there.
 *
 * The first four values are a CONTRACT with the backend's wire strings (see `ToStatusItem` in
 * `WorkflowEndpoints.cs`). `timedOut` is client-only — the server has no notion of the polling
 * budget.
 */
export type FileState = 'processing' | 'ready' | 'infected' | 'blocked' | 'timedOut';

/**
 * States an advisor cannot recover from by waiting. Each one means the file will never become
 * deliverable, so the only path forward is to remove the row and upload again.
 */
export const TERMINAL_BAD_SCAN_STATES: readonly FileState[] = ['infected', 'blocked', 'timedOut'];

/** Whether this file is in a scan state that must block the advisor from continuing. */
export function isScanBlocked(file: Pick<UploadedEntryFile, 'scanState'>): boolean {
  return file.scanState !== undefined && TERMINAL_BAD_SCAN_STATES.includes(file.scanState);
}

/**
 * Whether ANY uploaded file across the WHOLE store is scan-blocked — not just the active step's.
 *
 * The per-step gate (`isStepComplete`) only ever looks at the step the advisor is standing on, so
 * a file that regressed after its own step already passed (a delayed verdict flip, a re-scan)
 * would otherwise go unnoticed until BMO's own backstop rejects the submit. This is the
 * client-side mirror of that backstop (PROJECT_PLAN.md: "same gate re-applied at SUBMIT") — it
 * exists to fail fast, not to be the source of truth; the server re-checks delivery status
 * independently regardless of what this reports.
 */
export function hasBlockedAdditionalDocuments(store: UploadedFilesStore): boolean {
  return (
    Object.values(store.entries).some((files) => files.some(isScanBlocked)) ||
    store.bucket.some(isScanBlocked)
  );
}

/** One file inside a named mandatory / optional document entry. */
export type UploadedEntryFile = {
  /** Stable React key — name + size + lastModified + append-count. */
  uid: string;
  /** The actual File object — held so PdfGenerationStep can produce a
   *  blob URL on-demand for Preview / Download. */
  file: File;
  /** Absent in preview, and until the upload completes in advisor runtime. */
  uploadState?: UploadState;
  /** S3 key, once stored. Persisted against the advisor's saved state. */
  s3Key?: string;
  /** The name the object is stored under — the advisor's own filename, sanitized. */
  storedFileName?: string;
  /**
   * 1-based review order within the OneSpan package, assigned when the file was picked.
   *
   * Carried on the row rather than recomputed at delivery so the order the advisor built stays
   * the order BMO reviews in, even if a conditioned entry appears or disappears in between.
   */
  sequenceInPackage?: number;
  /**
   * Whether this file has reached BMO.
   *
   * <p>
   * The gate on editing its description. §14.1's DOCNAME is built from that description at the
   * deliver call and sent to FileX; once that has happened the name is spent, and an open box
   * would let the row describe one document while BMO files it under another. Before delivery
   * the description is free to change, because nothing has been composed from it yet.
   * </p>
   */
  deliveryState?: 'delivered';
  /** Message for a failed upload, shown on the row so the advisor can retry by re-picking. */
  uploadError?: string;
  /** Absent in preview, and until the upload succeeds in advisor runtime. */
  scanState?: FileState;
  /**
   * When this file started waiting for a verdict, as epoch ms. Each file carries its OWN clock
   * because files are uploaded at different moments — one shared window would cut short the budget
   * of a file added ten minutes into the step.
   */
  scanPollStartedAt?: number;
  /**
   * Raw scanner reason behind a `blocked`, e.g. `UNSUPPORTED`. Shown as supporting detail on the
   * row. Never a threat name — the server does not send those.
   */
  scanDetail?: string;
};

/**
 * One file inside the free-form optional bucket — carries the description the advisor gave it.
 *
 * The UI labels this "Description", matching what it is called on the wire to BMO
 * (`bmoData.supportingDocs[].description`, built in `buildESignSubmission`). The full text travels
 * there verbatim on every save, re-read out of the store each time.
 *
 * Its opening words have a SECOND role: they are §14.1's DOCNAME for this file, the one field BMO
 * tells one delivered document from another by.
 *
 * That name is composed SERVER-SIDE at the deliver call, not at upload — the object in S3 keeps
 * the advisor's own filename — which is what lets this stay editable for the whole time the
 * advisor is on the step. It closes only once the file has actually gone to BMO
 * (`deliveryState`), because at that point the name is spent and an open box would let the row
 * describe one document while BMO files it under another.
 */
export type UploadedBucketFile = UploadedEntryFile & {
  description: string;
};

/**
 * FormShell-owned store. `entries` keys on `StepFileEntry.id`; `bucket`
 * holds free-form advisor uploads with no per-file entry label.
 */
export type UploadedFilesStore = {
  entries: Record<string, UploadedEntryFile[]>;
  bucket: UploadedBucketFile[];
};

/** Compact view of a named mandatory-doc entry so callers don't have to
 *  re-walk the template step tree — the flattener consumes this and
 *  applies each entry's filename template at packaging time. */
export type UploadedEntryMeta = {
  label: string;
  filenameTemplate?: string | null;
};

/** Every named AdditionalDocuments entry on the template, keyed by entry id.
 *  Shared by the zip packager and the e-signature submission builder, which
 *  both need an entry's label to describe the files uploaded against it.
 *
 *  Deliberately the ENGLISH `label`, never `labelFr`: the e-signature description goes to BMO, and
 *  the English label is the DOCNAME it already identifies the document by (QA D-31). */
export function collectEntryMeta(
  template: Pick<WorkflowTemplate, 'steps'>,
): Record<string, UploadedEntryMeta> {
  const meta: Record<string, UploadedEntryMeta> = {};
  for (const step of template.steps) {
    if (step.type !== 'AdditionalDocuments') continue;
    for (const entry of step.additionalDocumentEntries ?? []) {
      meta[entry.id] = { label: entry.label, filenameTemplate: entry.filenameTemplate };
    }
  }
  return meta;
}

/** Flatten every uploaded file into a single list with a display label
 *  per entry — used by PdfGenerationStep to render rows and by the
 *  Download-all handler. Named entries expose their entry label; bucket
 *  files fall back to their filename. When an entry carries a
 *  `filenameTemplate`, the packaged `fileName` runs through the
 *  renderer so the zip archives it as e.g. `Transfer_Form_Smith_...pdf`
 *  instead of the original upload name. The file's original extension
 *  is preserved. Optional bucket files always keep their original name.
 */
export function flattenUploadedFiles(
  store: UploadedFilesStore,
  entryMeta: Record<string, UploadedEntryMeta>,
  values: Record<string, FieldValue> = {},
  workflowTemplate?: WorkflowTemplate,
): Array<{ uid: string; file: File; label: string; fileName: string }> {
  const out: Array<{ uid: string; file: File; label: string; fileName: string }> = [];
  for (const [entryId, files] of Object.entries(store.entries)) {
    const meta = entryMeta[entryId];
    const entryLabel = meta?.label ?? '';
    for (const f of files) {
      const originalExt = extensionOf(f.file.name);
      const packagedName =
        meta?.filenameTemplate && workflowTemplate
          ? renderFilename(
              meta.filenameTemplate,
              entryLabel || f.file.name,
              values,
              workflowTemplate,
              originalExt,
            )
          : f.file.name;
      out.push({
        uid: f.uid,
        file: f.file,
        label: entryLabel || f.file.name,
        fileName: packagedName,
      });
    }
  }
  for (const f of store.bucket) {
    // Optional bucket uploads: keep the original filename verbatim.
    out.push({
      uid: f.uid,
      file: f.file,
      label: f.file.name,
      fileName: f.file.name,
    });
  }
  return out;
}

/** Extract the extension (without the leading dot) from a filename;
 *  defaults to `pdf` when the file has no extension so the packaged
 *  name still lands under a recognisable type. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return 'pdf';
  return fileName.slice(dot + 1).toLowerCase();
}
